/**
 * FP3-093A — Admin notifications bell.
 *
 * Surfaces open "provider ended service" notifications on every admin
 * page (rendered in the AdminShell header). Clicking an item resolves
 * it and deep-links to the student's Team tab for review.
 *
 * FP3-119A — Every admin page renders its own <AdminShell>, so the
 * bell remounts on every route change and fired `/admin/notifications`
 * once per navigation (~90ms). We now dedupe with a module-level
 * TTL cache — subsequent mounts within `TTL_MS` reuse the last
 * result and skip the network round-trip. Actions that meaningfully
 * change the notification set (resolve, panel-open refresh) still
 * force a fresh fetch.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import api from "@/lib/api";

// FP3-119A — Module-level short-TTL cache keyed to the AdminShell
// bell. Not exported: the cache should not be observable from outside
// this component. Cleared on `logout()` via the `resetNotificationCache`
// hook wired into `auth.jsx`.
const TTL_MS = 15_000;
let _cache = { at: 0, rows: [], unread: 0 };
export function resetNotificationCache() {
  _cache = { at: 0, rows: [], unread: 0 };
}

export const AdminNotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(_cache.rows);
  const [unread, setUnread] = useState(_cache.unread);

  const load = (force = false) => {
    const fresh = Date.now() - _cache.at < TTL_MS;
    if (!force && fresh) {
      setRows(_cache.rows);
      setUnread(_cache.unread);
      return;
    }
    api.getAdminNotifications({ status: "new", limit: 15 })
      .then((r) => {
        const nextRows = r.data?.notifications || [];
        const nextUnread = r.data?.unread_count || 0;
        _cache = { at: Date.now(), rows: nextRows, unread: nextUnread };
        setRows(nextRows);
        setUnread(nextUnread);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    // Effect intentionally has no deps — the bell is stateless once
    // mounted; cache invalidation is time-based (TTL) or event-based
    // (resolve, panel open).
  }, []);

  const review = async (n) => {
    setOpen(false);
    try {
      await api.resolveAdminNotification(n.id);
    } catch (e) { /* still navigate */ }
    // Resolving mutates the notification set — force a fresh fetch.
    load(true);
    navigate(n.link || `/admin/students/${n.student_id}?tab=team`);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="admin-notifications-bell"
        title="Admin notifications"
        onClick={() => { setOpen((o) => !o); if (!open) load(true); }}
        className="relative h-9 w-9 rounded-lg border border-[#E5E3DB] bg-white hover:bg-[#F5F3EE] flex items-center justify-center text-[#1F2937]"
      >
        <Bell size={16} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="admin-notifications-count"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#B4530A] text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            data-testid="admin-notifications-panel"
            className="absolute right-0 top-full mt-1 z-40 w-96 bg-white border border-[#E5E3DB] rounded-xl shadow-lg overflow-hidden"
          >
            <p className="px-3 pt-2.5 pb-1.5 text-[9px] font-semibold uppercase tracking-wider text-[#9B9890] border-b border-[#F3F1EC]">
              Service changes needing review
            </p>
            {rows.length === 0 ? (
              <p className="px-3 py-4 text-xs text-[#6B7280]" data-testid="admin-notifications-empty">
                Nothing needs review right now.
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {rows.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`admin-notification-${n.id}`}
                    className="px-3 py-2.5 border-b border-[#F3F1EC] last:border-b-0"
                  >
                    <p className="text-xs text-[#1F2937]">{n.message}</p>
                    <p className="text-[10px] text-[#9B9890] mt-0.5">
                      {[n.campus, (n.created_at || "").slice(0, 10)]
                        .filter(Boolean).join(" · ")}
                    </p>
                    <button
                      type="button"
                      data-testid={`admin-notification-review-${n.id}`}
                      onClick={() => review(n)}
                      className="mt-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-[#1E3F33] text-white hover:bg-[#173127]"
                    >
                      {n.action_label || "Review student team"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminNotificationsBell;
